import {
  FormEvent,
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {useShareForwardedRef} from '../../../common/shared/hooks/useShareForwardedRef';
import {twMerge} from '../../../common/twMerge';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Input} from '../../atoms/Input/Input';
import {NavibarInputFieldProps} from './NavibarInputFieldProps.interface';

/**
 * 인풋필드 - 네비바에 해당합니다. 특정 컨텍스트에서만 사용되는 organism 입니다.
 * https://www.figma.com/file/mM95GNIeS97wxw0TmIBwIj/NEXT-Page-%EC%B5%9C%EC%A2%85-%EC%B7%A8%ED%95%A9_WEB?node-id=7138%3A279188
 */
export const NavibarInputField = forwardRef<
  HTMLInputElement,
  NavibarInputFieldProps
>(({onSubmit, isInvenSearch, onClickRemoveButton, ...inputProps}, ref) => {
  const inputRef = useShareForwardedRef(ref);

  const onClickRemoveText = useCallback(
    (e: MouseEvent) => {
      onClickRemoveButton?.(e);
      // x 버튼 동작 이후 input에 focus가 유지되어야 합니다.
      inputRef.current?.focus();
    },
    [inputRef, onClickRemoveButton]
  );
  const blurTimer = useRef(0);
  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit && onSubmit(event);
      // submit시점에 바로 blur해버리면 특정 키보드(ex. google keyboard) 에서 입력값을 한번 더 넣어버리는 문제가 있으므로 timeout을 걸어줍니다.
      blurTimer.current = window.setTimeout(
        () => inputRef.current?.blur(),
        // 요 시간은 검색 헤더에서 blur event 사용시 X버튼이 눌리지 않는 버그와도 관련 있습니다.
        400
      );
    },
    [inputRef, onSubmit]
  );

  useEffect(() => {
    return () => {
      window.clearTimeout(blurTimer.current);
    };
  }, []);

  return (
    <form
      onSubmit={handleSubmit}
      className={twMerge(
        'relative flex h-40pxr w-full items-center rounded-full bg-theme-solid-10 pr-10pxr pl-10pxr',
        isInvenSearch && 'pl-16pxr'
      )}
    >
      <Icon
        type={isInvenSearch ? IconType.SearchList : IconType.Search}
        className={twMerge('mr-4pxr', isInvenSearch && 'mr-8pxr')}
        disableActiveEffect
      />
      <Input
        ref={inputRef}
        {...inputProps}
        onClickRemoveButton={onClickRemoveText}
      />
    </form>
  );
});
